import moment from 'moment';
import uuid from 'uuid';

import { DEFAULT_LANG } from '@rover/constants/src/i18n/language';
import { I18N_FORMAT_MAP } from '@rover/kibble/styles';
import type { Day } from '@rover/types/src/datetime/Day';

import timeFactory from './timeFactory';

export function defaults(language?: string | null): Day {
  return {
    id: uuid.v4(),
    name: '',
    times: [timeFactory(undefined, language)],
  };
}

function dayFactory(day?: Partial<Day> | null, language?: string | null): Day {
  return { ...defaults(language), ...day };
}

export function createDayFromDate(
  aDate: Date,
  language?: string | null,
  dayNameFormat?: string | null,
  emptyTimes?: boolean
): Day {
  return dayFactory(
    {
      name: moment(aDate).format(
        dayNameFormat || I18N_FORMAT_MAP[language || DEFAULT_LANG].MONTH_DAY_MED
      ),
      date: aDate,
      id: uuid.v4(),
      times: emptyTimes ? [] : [timeFactory(undefined, language)],
    },
    language
  );
}

export function getDaysFromDateRangeInclusive(
  startRange: Date | null,
  endRange: Date | null,
  language?: string,
  dayNameFormat?: string,
  emptyTimes?: boolean
): Day[] {
  const days: Day[] = [];
  const current = moment(startRange).startOf('day');
  const last = moment(endRange).startOf('day');

  if (current.isValid() && last.isValid()) {
    while (current.isSameOrBefore(last)) {
      days.push(createDayFromDate(current.toDate(), language, dayNameFormat, emptyTimes));
      current.add(1, 'days');
    }
  }

  return days;
}

// create a Day object with a specific date when given a day of week index (sunday=0)
export function getDateFromDayOfWeekIndex(
  dayIndex: number,
  startDate?: Date,
  language?: string
): Day {
  const startingMoment = startDate ? moment(startDate) : moment();
  if (language) startingMoment.locale(language);
  const startingDay = startingMoment.day();
  let dayNumber = dayIndex;
  if (dayNumber < startingDay) dayNumber += 7;
  const wantedDay = startingMoment.day(dayNumber);
  const date = wantedDay.toDate();
  const name = wantedDay.format(I18N_FORMAT_MAP[language || DEFAULT_LANG].MONTH_DAY_MED);
  return dayFactory(
    {
      name,
      date,
      id: uuid.v4(),
      times: [timeFactory(undefined, language)],
    },
    language
  );
}

export default dayFactory;
